import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { filterTable } from '../shared/helpers/helpers';
import { FieldType } from '../shared/interfaces/field-type.enum';
import { IGenericTableRowDeletedEventData } from '../shared/interfaces/generic-table-row-deleted.interface';
import { GenericTableColumn } from '../shared/interfaces/genetic-table-column';
import { RowType } from '../shared/interfaces/raw-type.enum';
import { AuthState } from '../shared/states/auth/auth.state';
import { DeleteUserMessage, GetAllMessages, MarkMessageRead } from '../shared/states/user-message/user-message.actions';
import { UserMessageState } from '../shared/states/user-message/user-message.state';
import { IUserMessageIndexModel } from './interfaces/user-message-index-model.interface';

@Component({
  selector: 'app-messages',
  templateUrl: './messages.component.html',
  styleUrls: ['./messages.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
@UntilDestroy()
export class MessagesComponent implements OnInit {
  @Select(UserMessageState.allMessages) messages$: Observable<IUserMessageIndexModel[]>;
  @Select(UserMessageState.loading) loading$: Observable<boolean>;

  columns: GenericTableColumn<IUserMessageIndexModel>[] = [];
  
  filtered$: BehaviorSubject<IUserMessageIndexModel[]> = new BehaviorSubject([]);
  dataLength: number;
  showDeleteConfirmationDialog$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  deleteMessageEventData: IGenericTableRowDeletedEventData<IUserMessageIndexModel>;
  
  expandedMessage: IUserMessageIndexModel;
  searchValue: string = '';
  /**
   *
   */
  constructor(
    private store: Store,
    private cdr: ChangeDetectorRef
    ) {
    this.initTableColumns();
  }

  ngOnInit(): void {
    const activeUserId = this.store.selectSnapshot(AuthState.activeUser).id;
    this.store.dispatch(new GetAllMessages(activeUserId));
  
    this.messages$
    .pipe(untilDestroyed(this))
    .subscribe(messages => {
      this.searchValueChanged(this.searchValue);
      this.dataLength = messages?.length;
      this.cdr.markForCheck();
    });
  }

  readMore(message: IUserMessageIndexModel): void {
    this.expandedMessage = message;
  }

  deleteMessage(data: IGenericTableRowDeletedEventData<IUserMessageIndexModel>): void {
    this.deleteMessageEventData = data;
    this.showDeleteConfirmationDialog$.next(true);
  }

  deleteConfirmed(): void {
    if (this.deleteMessageEventData) {
      this.store.dispatch(new DeleteUserMessage(this.deleteMessageEventData.item.userMessageId, this.deleteMessageEventData.item.messageId));
      this.deleteMessageEventData = null;
      this.showDeleteConfirmationDialog$.next(false);
    }
  }

  gotIt(message: IUserMessageIndexModel): void {
    if (message.messageId && !message.readOn) {
     const activeUserId = this.store.selectSnapshot(AuthState.activeUser)?.id;
     this.store.dispatch(new MarkMessageRead(activeUserId, message.messageId));
    }
    this.expandedMessage = null;
    this.cdr.markForCheck();
  }

  searchValueChanged(val: string): void {
    this.searchValue = val;
    const filtered = filterTable(
      this.store.selectSnapshot(UserMessageState.allMessages) || [],
      val,
      'title',
      'body',
    );
    this.filtered$.next(filtered);
  }

  private initTableColumns(): void {
    const dateTimeStyle = { 'width': '150px'};
     this.columns = [
           new GenericTableColumn<IUserMessageIndexModel>({
                header: 'Message',
                field: 'title',
                RowType: RowType.Template,
                style: {width: '70%'}
              }),
              new GenericTableColumn<IUserMessageIndexModel>({
                    header: 'Received',
                    field: 'createDate',
                    fieldType: FieldType.DateTime,
                    style: dateTimeStyle
              }),
              new GenericTableColumn<IUserMessageIndexModel>({
                header: 'Read',
                field: 'readOn',
                fieldType: FieldType.DateTime,
                style: dateTimeStyle,
          }),
          new GenericTableColumn<IUserMessageIndexModel>({
            header: '',
            RowType: RowType.RemoveRowButton,
            sortable: false,
            style: {width: '75px'}
          }),
    ];
  }
}
